import { Button, Popconfirm } from 'antd'
import classNames from 'classnames'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import Image from 'next/image'
import { AudioOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { createGlobalState } from 'react-use'

import { GENDER, Gender } from '@/constants/gender'
import { poppins } from '@/components/common/fonts'
import { IconPreviewPause, IconPreviewPlay } from '@/components/common/Icons'
import { useDeleteVoiceMutation } from '@/redux/api/myavatarApi'

import { VoiceEditModal } from './VoiceEditModal'

/**
 * 미리듣기 재생 관련
 */
const usePlayingUniqueId = createGlobalState('')

export const VOICE_TYPE = {
  ORIGINAL: 'ORIGINAL',
  TTS: 'TTS',
  VOICE_CLONE: 'VOICE_CLONE',
} as const
export type VoiceType = (typeof VOICE_TYPE)[keyof typeof VOICE_TYPE]

type VoiceCardPropsBase = {
  voiceId: string
  title: string
  isSelected?: boolean
  onClick?: () => void
  sampleUrl?: string

  className?: string
}

type VoiceCardCloningProps = VoiceCardPropsBase & {
  type: typeof VOICE_TYPE.VOICE_CLONE
  isSample?: boolean
  gender: Gender

  onDeleteVoiceClone?: (voiceId: string) => void
}

type VoiceCardModelOriginalProps = VoiceCardPropsBase & {
  type: typeof VOICE_TYPE.ORIGINAL
  modelName: string
  /**
   * ex: en-US, en, US, ...
   */
  language: string
}

type VoiceCardTtsProps = VoiceCardPropsBase & {
  type: typeof VOICE_TYPE.TTS
  gender: Gender
  description: string
  /**
   * ex: en-US, en, US, ...
   */
  language: string
}

type VoiceCardProps =
  | VoiceCardCloningProps
  | VoiceCardModelOriginalProps
  | VoiceCardTtsProps

export const VoiceCard = forwardRef<HTMLDivElement, VoiceCardProps>(
  (props, forwardedRef) => {
    const { t } = useTranslation('common')

    const {
      voiceId,
      title,
      isSelected,
      onClick,
      sampleUrl,
      type,

      className,
    } = props

    const [isOpenedVoiceEditModal, setIsOpenedVoiceEditModal] = useState(false)

    const [mutateDeleteVoice, { isLoading: isMutatingDeleteVoice }] =
      useDeleteVoiceMutation()

    const handleDeleteVoice = useCallback(async () => {
      if (isMutatingDeleteVoice) {
        return
      }

      await mutateDeleteVoice(voiceId).unwrap()

      if (type === VOICE_TYPE.VOICE_CLONE) {
        props.onDeleteVoiceClone?.(voiceId)
      }
    }, [voiceId, mutateDeleteVoice, isMutatingDeleteVoice, type, props])

    const isVoiceCloning = type === VOICE_TYPE.VOICE_CLONE
    const isOriginal = type === VOICE_TYPE.ORIGINAL

    const gender =
      type === VOICE_TYPE.TTS || type === VOICE_TYPE.VOICE_CLONE
        ? props.gender
        : ''

    const genderText = useMemo(() => {
      if (!gender) {
        return ''
      }

      if (gender === GENDER.MALE) {
        return t('Male')
      }

      if (gender === GENDER.FEMALE) {
        return t('Female')
      }
    }, [t, gender])

    const descriptionTts = type === VOICE_TYPE.TTS && props.description
    const descriptionVoiceCloning =
      type === VOICE_TYPE.VOICE_CLONE && t('Voice Cloning')
    const descriptionOriginal = type === VOICE_TYPE.ORIGINAL && props.modelName

    const description =
      descriptionTts || descriptionVoiceCloning || descriptionOriginal

    const language =
      type === VOICE_TYPE.ORIGINAL || type === VOICE_TYPE.TTS
        ? props.language
        : ''

    const flagUrl = useMemo(() => {
      if (!language) {
        return ''
      }

      const [languageCode, countryCode] = language
        .toLocaleLowerCase()
        .split('-')

      if (!!countryCode) {
        return `/images/flags/country/${countryCode}.svg`
      }

      if (!!languageCode) {
        return `/images/flags/language/${languageCode}.svg`
      }

      return ''
    }, [language])

    const isCloningSample = type === VOICE_TYPE.VOICE_CLONE && props.isSample

    const headIcon = useMemo(() => {
      if (isCloningSample) {
        return <VoiceCloningSample>{t('Sample')}</VoiceCloningSample>
      }

      if (isVoiceCloning) {
        return <AudioOutlined />
      }

      if (!language) {
        return <AudioOutlined />
      }

      if (language === 'multi') {
        return <AudioOutlined />
      }

      const [languageCode, countryCode] = language
        .toLocaleLowerCase()
        .split('-')

      if (!!countryCode) {
        return (
          <Image
            src={`/images/flags/country/${countryCode}.svg`}
            alt={language}
            width={24}
            height={16}
          />
        )
      }

      if (!!languageCode) {
        return (
          <Image
            src={`/images/flags/language/${languageCode}.svg`}
            alt={language}
            width={24}
            height={16}
          />
        )
      }

      return <AudioOutlined />
    }, [isCloningSample, isVoiceCloning, language, t])

    /**
     * 미리듣기 재생 관련
     */
    const playerUniqueId = useMemo(() => Math.random().toString(), [])

    const [isPlaying, setIsPlaying] = useState(false)

    const [playingUniqueId, setPlayingUniqueId] = usePlayingUniqueId()

    const playerRef = useRef<HTMLAudioElement>(null)

    const play = useCallback(() => {
      playerRef.current?.play()
    }, [])

    const stop = useCallback(() => {
      if (!playerRef.current) {
        return
      }

      playerRef.current.pause()
      playerRef.current.currentTime = 0
    }, [])

    const handlePlayToggle = useCallback(() => {
      if (!playerRef.current) {
        return
      }

      if (playerRef.current.paused) {
        play()
      } else {
        stop()
      }
    }, [play, stop])

    useEffect(() => {
      if (playingUniqueId !== playerUniqueId) {
        stop()
      }
    }, [playingUniqueId, playerUniqueId, stop])

    useEffect(() => {
      const handlePlay = () => {
        setPlayingUniqueId(playerUniqueId)
        setIsPlaying(true)
      }
      const handlePause = () => {
        if (playingUniqueId === playerUniqueId) {
          setPlayingUniqueId('')
        }
        setIsPlaying(false)
      }

      const audioEl = playerRef.current

      if (audioEl) {
        audioEl.addEventListener('play', handlePlay)
        audioEl.addEventListener('pause', handlePause)
      }

      return () => {
        if (audioEl) {
          audioEl.removeEventListener('play', handlePlay)
          audioEl.removeEventListener('pause', handlePause)
        }
      }
    }, [playerUniqueId, playingUniqueId, setPlayingUniqueId])

    return (
      <VoiceCardContainer
        ref={forwardedRef}
        className={classNames(className, {
          selected: isSelected,
        })}
        onClick={(e) => {
          if (!e.currentTarget.contains(e.target as Node)) {
            return
          }
          onClick?.()
        }}
      >
        <Header>
          <HeaderIcon>{headIcon}</HeaderIcon>
          <HeaderTitle
            className={classNames({
              'voice-cloning': isVoiceCloning,
            })}
            title={title}
          >
            {title}
          </HeaderTitle>

          {!!isVoiceCloning && !isCloningSample && (
            <HeaderControls>
              <VoiceCloningEditingButton
                onClick={(e) => {
                  e.stopPropagation()
                  setIsOpenedVoiceEditModal(true)
                }}
                icon={<EditOutlined />}
              />

              <Popconfirm
                title={t('Delete voice')}
                description={t('Are you sure to delete this voice?')}
                okText={t('Yes')}
                cancelText={t('No')}
                onCancel={(e) => {
                  e?.stopPropagation()
                }}
                onConfirm={async (e) => {
                  e?.stopPropagation()
                  await handleDeleteVoice()
                }}
              >
                <VoiceCloningEditingButton
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </HeaderControls>
          )}
        </Header>
        <Content>
          <DescriptionArea>
            <Description>
              {isOriginal && (
                <ModelOriginalVoice>
                  {t("Model's Original Voice")}
                </ModelOriginalVoice>
              )}

              {!isOriginal && genderText}
            </Description>
            <Description>{description}</Description>
          </DescriptionArea>

          {!!sampleUrl && (
            <PlayButton
              onClick={(e) => {
                e.stopPropagation()
                handlePlayToggle()
              }}
            >
              {isPlaying ? <IconPreviewPause /> : <IconPreviewPlay />}
            </PlayButton>
          )}
        </Content>

        {type === VOICE_TYPE.VOICE_CLONE && !props.isSample && (
          <VoiceEditModal
            isOpened={isOpenedVoiceEditModal}
            onClose={() => setIsOpenedVoiceEditModal(false)}
            voiceId={voiceId}
            voiceName={props.title}
            gender={props.gender}
          />
        )}

        {!!sampleUrl && (
          <audio
            ref={playerRef}
            src={sampleUrl}
            style={{ display: 'none' }}
            preload="none"
          />
        )}
      </VoiceCardContainer>
    )
  },
)

/**
 * &.selected
 */
const VoiceCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 6px;

  position: relative;

  height: 88px;
  padding: 15px 20px;

  border: 1px solid #cccccc80;
  border-radius: 8px;
  box-sizing: border-box;

  cursor: pointer;

  transition: box-shadow 0.2s, border 0.2s;

  &:hover {
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;

    border-radius: 8px;
    border: 2px solid transparent;

    transition: border 0.2s;

    pointer-events: none;
  }

  &.selected {
    &::before {
      border-color: #4276ee;
    }
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const VoiceCloningSample = styled.div`
  border-radius: 4px;
  padding-inline: 6px;
  background-color: #4564ac;

  font-family: ${poppins.string};
  font-size: 10px;
  font-weight: 500;
  line-height: 20px;
  color: #ffffff;
`

/**
 * &.voice-cloning
 */
const HeaderTitle = styled.div`
  flex: 1;

  font-family: ${poppins.string};
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  color: #222222;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.voice-cloning {
    color: #4e77e6;
  }
`

const HeaderControls = styled.div`
  display: flex;
  gap: 4px;
  opacity: 0;

  transition: opacity 0.2s;

  ${VoiceCardContainer}:hover & {
    opacity: 1;
  }
`

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
`

const DescriptionArea = styled.div`
  display: flex;
  flex-direction: column;

  font-family: ${poppins.string};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #777777;

  overflow: hidden;
`

const Description = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ModelOriginalVoice = styled.span`
  padding: 2px 6px 2px 6px;
  background-color: #e2fd52;

  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  color: #777777;
`

const PlayButton = styled(Button)`
  border-radius: 9999px;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;

  transition: box-shadow 0.2s;

  circle {
    fill: #4276ee4d;
  }

  path,
  rect {
    fill: #4276ee;
  }

  &:hover {
    box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.2);
  }
`

const VoiceCloningEditingButton = styled(Button)`
  border: 0;
  color: #666666;

  &&&:hover {
    background-color: #f5f5f5;
    color: #666666;
  }
`
